// ABOUTME: Dashboard service for guest (unauthenticated) users using localStorage.
// ABOUTME: Mirrors the dashboardService API but persists entirely client-side.

import type { DashboardItem } from "./dashboardService";

export const GUEST_DASHBOARD_KEY = "guest_dashboard";
export const GUEST_MODE_KEY = "guest_mode";

export const guestDashboardService = {
  isGuestMode(): boolean {
    return localStorage.getItem(GUEST_MODE_KEY) === "true";
  },

  enterGuestMode(): void {
    localStorage.setItem(GUEST_MODE_KEY, "true");
  },

  exitGuestMode(): void {
    localStorage.removeItem(GUEST_MODE_KEY);
  },

  getDashboard(): DashboardItem[] {
    try {
      const raw = localStorage.getItem(GUEST_DASHBOARD_KEY);
      if (!raw) return [];
      return JSON.parse(raw) as DashboardItem[];
    } catch {
      try {
        localStorage.removeItem(GUEST_DASHBOARD_KEY);
      } catch {
        // localStorage unavailable — fall through to the empty dashboard.
      }
      return [];
    }
  },

  setDashboard(items: DashboardItem[]): void {
    localStorage.setItem(GUEST_DASHBOARD_KEY, JSON.stringify(items));
  },

  addItem(item: Omit<DashboardItem, "id">): string {
    const items = this.getDashboard();
    const id = crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newItem: DashboardItem = {
      ...item,
      id,
      createdDate: Date.now(),
    };
    items.push(newItem);
    this.setDashboard(items);
    return id;
  },

  updateItem(itemId: string, updates: Partial<DashboardItem>): void {
    const items = this.getDashboard();
    const updated = items.map((item) => (item.id === itemId ? { ...item, ...updates } : item));
    this.setDashboard(updated);
  },

  deleteItem(itemId: string): void {
    const items = this.getDashboard();
    this.setDashboard(items.filter((item) => item.id !== itemId));
  },

  deleteAllLinks(): void {
    const items = this.getDashboard();
    this.setDashboard(items.filter((item) => item.type !== "link"));
  },

  getItemsForMigration(): DashboardItem[] {
    return this.getDashboard();
  },

  clearAll(): void {
    localStorage.removeItem(GUEST_DASHBOARD_KEY);
    localStorage.removeItem(GUEST_MODE_KEY);
  },
};
